// MorrowOS — Connections tab.
//
// A Composio connector / MCP browser. Users search across the thousands of
// Composio toolkits (Gmail, Slack, Notion, GitHub, Linear, …) and hit Connect
// to run the managed-OAuth popup flow, after which the app can chat against
// that app's tools.
//
// Search is backed by the paged server route
// GET /api/morrow/composio/toolkits?q=…&dept=…&offset=…&limit=…
// (in apps/server/src/morrow/composioRoutes.ts), which returns
// { toolkits: [{ slug, name, logo }], total, offset, limit }. The catalog holds
// ~1046 toolkits, so the tab renders a responsive CARD GRID with infinite
// scroll: it appends one page at a time (auto-loading via an IntersectionObserver
// sentinel plus a manual "Load more") until the (optionally filtered) catalog is
// exhausted. Connecting reuses the same managed-OAuth endpoint the landing uses
// (POST /api/morrow/composio/connect) via composioConnect's resolveComposioUserId
// + openConnectPopup helpers. Already-connected toolkits are read from
// GET /api/morrow/composio/connections.

import { Link } from "@tanstack/react-router";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import {
  ActivityIcon,
  CalendarIcon,
  CheckIcon,
  DatabaseIcon,
  EllipsisIcon,
  FileCode2Icon,
  GlobeIcon,
  LayersIcon,
  Loader2Icon,
  LogInIcon,
  MailIcon,
  MessageSquareIcon,
  SearchIcon,
  ServerIcon,
  SparklesIcon,
  UsersIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type ConnectOutcome,
  connectOutcomeFrom,
  openPendingConnectPopup,
  pointConnectPopup,
  resolveComposioUserId,
  startDisconnect,
} from "../../composioConnect";
import { apiUrl } from "../../apiBase";
import { openUpgradeModal, parseGateUpgrade } from "../../upgradeModal";
import { cn } from "../../lib/utils";
import { useI18n } from "~/i18n";
import { Input } from "../ui/input";
import {
  hasMoreToolkits,
  nextToolkitOffset,
  TOOLKIT_DEPARTMENT_FILTERS,
  type ToolkitDepartmentFilterId,
} from "./ConnectionsSettings.logic";
import {
  AppConnectDialog,
  BuiltinAppLogo,
  disconnectBuiltinApp,
  fetchAppConnections,
  fetchBuiltinApps,
  type AppConnection,
  type BuiltinAppInfo,
} from "./AppConnectPage";
import { builtinAppMatches } from "./builtinApps";
import { connectOAuthPopup, fetchMcpServers } from "./McpConnectPage";
import { toolkitTaglineKey } from "./toolkitTaglines";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";

type ComposioToolkit = { slug: string; name: string; logo: string };

/** One page of catalog results plus the total behind the current filter. */
type ToolkitPage = { toolkits: ComposioToolkit[]; total: number };

const PAGE_LIMIT = 60;

const DEPARTMENT_ICONS: Record<ToolkitDepartmentFilterId, LucideIcon> = {
  all: LayersIcon,
  comms: MessageSquareIcon,
  dev: FileCode2Icon,
  work: CalendarIcon,
  crm: UsersIcon,
  data: DatabaseIcon,
  finance: ActivityIcon,
  ai: SparklesIcon,
  other: EllipsisIcon,
};

async function fetchToolkitPage(
  query: string,
  department: ToolkitDepartmentFilterId,
  offset: number,
  signal: AbortSignal,
): Promise<ToolkitPage> {
  const params = new URLSearchParams({
    q: query,
    offset: String(offset),
    limit: String(PAGE_LIMIT),
  });
  if (department !== "all") params.set("dept", department);
  const res = await fetch(apiUrl(`/api/morrow/composio/toolkits?${params.toString()}`), {
    headers: { accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new Error(`toolkits fetch failed: ${res.status}`);
  const data = (await res.json()) as { toolkits?: unknown; total?: unknown };
  const toolkits = Array.isArray(data.toolkits)
    ? data.toolkits.filter(
        (t): t is ComposioToolkit =>
          !!t &&
          typeof t === "object" &&
          typeof (t as ComposioToolkit).slug === "string" &&
          typeof (t as ComposioToolkit).name === "string",
      )
    : [];
  const total = typeof data.total === "number" ? data.total : toolkits.length;
  return { toolkits, total };
}

// Returns null for "UNKNOWN — keep what's shown" (guest/empty id, or a failed/
// malformed fetch), vs a Set for an authoritative answer. Prevents a signed-in
// user's connected apps from blanking to "Connect" on a transient error or before
// Clerk confirms sign-in — connections persist in Composio under the `user_` id.
async function fetchConnectedSlugs(userId: string): Promise<Set<string> | null> {
  if (!userId || !userId.startsWith("user_")) return null;
  try {
    const res = await fetch(
      apiUrl(`/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`),
      { headers: { accept: "application/json" } },
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { connected?: unknown };
    if (!Array.isArray(data.connected)) return null;
    return new Set(data.connected.filter((s): s is string => typeof s === "string"));
  } catch {
    return null;
  }
}

/** Begin the managed-OAuth flow for an arbitrary Composio toolkit slug. */
async function startConnectSlug(slug: string, userId: string): Promise<ConnectOutcome> {
  try {
    const res = await fetch(apiUrl("/api/morrow/composio/connect"), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ toolkit: slug, userId }),
    });
    // Plan gate → surface the upgrade modal (message carries the capability).
    if (res.status === 402) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      const gate = parseGateUpgrade(body?.error);
      openUpgradeModal(
        gate
          ? { capability: gate.capability, message: gate.message, reason: gate.reason }
          : { capability: "tools.catalog.full" },
      );
      return { kind: "failed" };
    }
    if (!res.ok) return { kind: "failed" };
    return connectOutcomeFrom(await res.json());
  } catch {
    return { kind: "failed" };
  }
}

/**
 * The toolkit logo tile — the real Composio logo on a neutral white square (for
 * crisp contrast across themes), with a monogram fallback if the remote image
 * fails to load.
 */
function ToolkitLogo({ toolkit }: { toolkit: ComposioToolkit }) {
  const { t } = useI18n();
  const [failed, setFailed] = useState(false);
  const src = toolkit.logo || `https://logos.composio.dev/api/${toolkit.slug}`;
  if (failed || !src) {
    return (
      <span className="flex size-12 items-center justify-center rounded-lg border border-border bg-muted text-sm font-semibold uppercase text-muted-foreground">
        {toolkit.name.slice(0, 2)}
      </span>
    );
  }
  return (
    <div className="flex size-12 items-center justify-center rounded-lg border border-border bg-white dark:bg-white">
      <img
        src={src}
        alt={t("settings.connections.logoAlt", { name: toolkit.name })}
        loading="lazy"
        onError={() => setFailed(true)}
        className="size-7 object-contain"
      />
    </div>
  );
}

/**
 * A single connector card: logo on top, name + capability line, and a centered
 * "Connect" button (or a green "Connected" state) pinned to the bottom.
 */
function ToolkitCard({
  toolkit,
  connected,
  connecting,
  disconnecting,
  onConnect,
  onDisconnect,
}: {
  toolkit: ComposioToolkit;
  connected: boolean;
  connecting: boolean;
  disconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)]">
      <ToolkitLogo toolkit={toolkit} />
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">{toolkit.name}</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {t(toolkitTaglineKey(toolkit.slug))}
        </div>
      </div>
      {connected ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
            <CheckIcon className="size-3.5" aria-hidden="true" />
            {t("settings.connections.connected")}
          </span>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            aria-label={t("settings.connections.disconnectApp", { name: toolkit.name })}
            title={t("settings.connections.disconnect")}
            className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            {disconnecting ? (
              <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <XIcon className="size-3.5" aria-hidden="true" />
            )}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onConnect}
          disabled={connecting}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity",
            "hover:opacity-90 disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {connecting ? (
            <>
              <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
              {t("settings.connections.connecting")}
            </>
          ) : (
            t("settings.connections.connect")
          )}
        </button>
      )}
    </div>
  );
}

/**
 * The BYO email (IMAP/SMTP) entry, styled identically to a connector card so it
 * sits in the grid alongside Gmail/Slack/etc. Navigates to the managed connect
 * page rather than kicking off a Composio OAuth flow.
 */
function EmailConnectCard({ connectedCount }: { connectedCount: number }) {
  const { t } = useI18n();
  const isConnected = connectedCount > 0;
  return (
    <Link
      to="/settings/email-connect"
      className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)]"
    >
      <div className="flex size-12 items-center justify-center rounded-lg border border-border bg-white dark:bg-white">
        <MailIcon className="size-7 text-[#1a73e8]" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">Business Email</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {isConnected
            ? connectedCount === 1
              ? t("settings.connections.tagline.businessEmailOneMailbox")
              : t("settings.connections.tagline.businessEmailMailboxes", {
                  count: connectedCount,
                })
            : t("settings.connections.tagline.businessEmail")}
        </div>
      </div>
      {isConnected ? (
        <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
          <CheckIcon className="size-3.5" aria-hidden="true" />
          Connected
        </span>
      ) : (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity",
            "hover:opacity-90",
          )}
        >
          Connect
        </span>
      )}
    </Link>
  );
}

/**
 * A built-in app (DIA, Entegra) — the same card as a Composio toolkit: Connect
 * opens a sign-in dialog right here; once connected, an inline ✕ disconnects.
 */
function BuiltinAppCard({
  app,
  connection,
  disconnecting,
  onConnect,
  onDisconnect,
}: {
  app: BuiltinAppInfo;
  connection: AppConnection | undefined;
  disconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)]">
      <BuiltinAppLogo app={app} />
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">{app.name}</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {connection ? connection.accountLabel : app.description}
        </div>
      </div>
      {connection ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
            <CheckIcon className="size-3.5" aria-hidden="true" />
            {t("settings.connections.connected")}
          </span>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            aria-label={t("settings.connections.disconnectApp", { name: app.name })}
            title={t("settings.connections.disconnect")}
            className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            {disconnecting ? (
              <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <XIcon className="size-3.5" aria-hidden="true" />
            )}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onConnect}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity",
            "hover:opacity-90 disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {t("settings.connections.connect")}
        </button>
      )}
    </div>
  );
}

/** "Your own MCP server" — a connector card linking to the MCP connect page. */
function McpConnectCard({ connectedCount }: { connectedCount: number }) {
  const isConnected = connectedCount > 0;
  return (
    <Link
      to="/settings/mcp-connect"
      className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)]"
    >
      <div className="flex size-12 items-center justify-center rounded-lg border border-border bg-muted">
        <ServerIcon className="size-7 text-foreground" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">Custom MCP</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {isConnected
            ? `${connectedCount} server${connectedCount === 1 ? "" : "s"} connected`
            : "Bring tools from your own MCP server"}
        </div>
      </div>
      {isConnected ? (
        <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
          <CheckIcon className="size-3.5" aria-hidden="true" />
          Connected
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90">
          Connect
        </span>
      )}
    </Link>
  );
}

/**
 * Browser-control entry — a connector card (styled like the others) that links
 * the browser-loop extension so the agent can act in the user's real browser.
 * Navigates to the managed pair page; shows a live Connected state when online.
 */
function BrowserConnectCard({ online }: { online: boolean }) {
  const { t } = useI18n();
  return (
    <Link
      to="/settings/browser-connect"
      className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)]"
    >
      <div className="flex size-12 items-center justify-center rounded-lg border border-border bg-white dark:bg-white">
        <GlobeIcon className="size-7 text-[#2563eb]" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">Browser</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {online
            ? t("settings.connections.tagline.browserConnected")
            : t("settings.connections.tagline.browser")}
        </div>
      </div>
      {online ? (
        <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
          <CheckIcon className="size-3.5" aria-hidden="true" />
          Connected
        </span>
      ) : (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity",
            "hover:opacity-90",
          )}
        >
          Connect
        </span>
      )}
    </Link>
  );
}

/**
 * Personal WhatsApp entry — a connector card that links your OWN WhatsApp as a
 * device so the agent reads/sends on your behalf. Navigates to the managed QR
 * link page. Shows a Connected badge when linked (light status poll).
 */
function WhatsAppConnectCard() {
  const { t } = useI18n();
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(apiUrl("/api/morrow/whatsapp-personal/status"), {
          headers: { accept: "application/json" },
        });
        if (res.ok && alive) {
          const data = (await res.json()) as { status?: string };
          setConnected(data?.status === "connected");
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  return (
    <Link
      to="/settings/whatsapp-connect"
      className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)]"
    >
      <div className="flex size-12 items-center justify-center rounded-lg border border-border bg-white dark:bg-white">
        <img
          src="https://logos.composio.dev/api/whatsapp"
          alt=""
          aria-hidden="true"
          className="size-7 object-contain"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">WhatsApp</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {t("settings.connections.tagline.whatsapp")}
        </div>
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
          <CheckIcon className="size-3.5" aria-hidden="true" />
          {t("settings.connections.connected")}
        </span>
      ) : (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity",
            "hover:opacity-90",
          )}
        >
          Connect
        </span>
      )}
    </Link>
  );
}

/**
 * Plaud — a curated one-click OAuth MCP connector, sitting in the grid next to the
 * other apps (not buried on the Custom-MCP page). Clicking Connect runs the same
 * OAuth popup the Custom-MCP page uses (sign in to Plaud → tokens stored encrypted),
 * then re-checks whether a Plaud server is now attached.
 */
const PLAUD_MCP_URL = "https://mcp.plaud.ai/mcp";
function PlaudConnectCard() {
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoOk, setLogoOk] = useState(true);
  const check = async () => {
    try {
      const servers = await fetchMcpServers();
      setConnected(servers.some((s) => s.url === PLAUD_MCP_URL || /plaud/i.test(s.name)));
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    void check();
  }, []);
  const onConnect = async () => {
    setBusy(true);
    setError(null);
    const err = await connectOAuthPopup(PLAUD_MCP_URL, "Plaud");
    setBusy(false);
    if (err) setError(err);
    else await check();
  };
  return (
    <button
      type="button"
      onClick={() => void onConnect()}
      disabled={busy}
      className="card-glass flex flex-col items-center gap-3 rounded-lg border border-transparent p-4 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--fnr-card-hover-shadow)] disabled:opacity-70"
    >
      <div className="flex size-12 items-center justify-center rounded-lg border border-border bg-white dark:bg-white">
        {logoOk ? (
          <img
            src="https://www.google.com/s2/favicons?domain=plaud.ai&sz=128"
            alt=""
            aria-hidden="true"
            className="size-7 object-contain"
            onError={() => setLogoOk(false)}
          />
        ) : (
          <span className="text-lg font-black text-[#6D28D9]">P</span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
        <div className="line-clamp-2 text-sm font-bold text-foreground">Plaud</div>
        <div className="line-clamp-2 w-full text-xs text-muted-foreground/70">
          {error ?? "Your Plaud recordings, transcripts and summaries"}
        </div>
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1 font-bold text-green-600 text-xs uppercase dark:text-green-500">
          <CheckIcon className="size-3.5" aria-hidden="true" />
          Connected
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90">
          {busy ? "Connecting…" : "Connect"}
        </span>
      )}
    </button>
  );
}

export function ConnectionsSettings() {
  const { t } = useI18n();
  const {
    isLoaded: isClerkLoaded,
    isSignedIn,
    userId: clerkUserId,
    openSignIn,
  } = useMorrowClerkAuth();
  const clerk = useMemo(() => ({ openSignIn }), [openSignIn]);
  // A signed-out guest may VIEW the connector catalog but cannot connect an app
  // (connecting is a persisted per-user integration). When they try, we surface
  // an inline "sign in to connect" prompt and open Clerk instead.
  const [signInPrompt, setSignInPrompt] = useState(false);
  const userId = useMemo(
    () => resolveComposioUserId(isClerkLoaded && isSignedIn ? clerkUserId : null),
    [isClerkLoaded, isSignedIn, clerkUserId],
  );

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [department, setDepartment] = useState<ToolkitDepartmentFilterId>("all");
  const [toolkits, setToolkits] = useState<ComposioToolkit[]>([]);
  const [total, setTotal] = useState(0);
  // `loading` starts true so the first catalog fetch doesn't flash an empty grid.
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectedSlugs, setConnectedSlugs] = useState<Set<string>>(new Set());
  const [connectingSlug, setConnectingSlug] = useState<string | null>(null);
  const [disconnectingSlug, setDisconnectingSlug] = useState<string | null>(null);
  // Count of connected BYO email (IMAP/SMTP) mailboxes — email lives outside the
  // Composio connection list, so the Business Email card reads it separately to
  // show a real "Connected" state instead of always showing "Connect".
  const [emailAccountCount, setEmailAccountCount] = useState<number | null>(null);
  // Attached MCP servers (signed-in only) for the Custom MCP card's state.
  const [mcpServerCount, setMcpServerCount] = useState(0);
  // Built-in apps (DIA, Entegra) and which of them this person has connected.
  const [builtinApps, setBuiltinApps] = useState<BuiltinAppInfo[]>([]);
  const [appConnections, setAppConnections] = useState<AppConnection[]>([]);
  // The built-in app whose Connect dialog is open, and one being disconnected.
  const [connectApp, setConnectApp] = useState<BuiltinAppInfo | null>(null);
  const [disconnectingApp, setDisconnectingApp] = useState<string | null>(null);
  const visibleBuiltinApps = useMemo(
    () => builtinApps.filter((app) => builtinAppMatches(app, debouncedQuery, department)),
    [builtinApps, debouncedQuery, department],
  );
  const handleBuiltinConnect = useCallback(
    (app: BuiltinAppInfo) => {
      if (isClerkLoaded && !isSignedIn) {
        clerk.openSignIn();
        return;
      }
      setConnectApp(app);
    },
    [clerk, isClerkLoaded, isSignedIn],
  );
  const handleBuiltinDisconnect = useCallback(async (appId: string) => {
    setDisconnectingApp(appId);
    if (await disconnectBuiltinApp(appId)) {
      setAppConnections((list) => list.filter((c) => c.appId !== appId));
    }
    setDisconnectingApp(null);
  }, []);
  // Whether a browser (browser-loop extension) is currently paired + online.
  const [browserOnline, setBrowserOnline] = useState<boolean>(false);

  const hasMore = hasMoreToolkits(toolkits.length, total);
  const trimmedQuery = query.trim();

  // Load the user's already-connected toolkits once, so cards can show a
  // "Connected" state instead of a Connect button.
  useEffect(() => {
    let cancelled = false;
    void fetchConnectedSlugs(userId).then((slugs) => {
      if (!cancelled && slugs) setConnectedSlugs(slugs); // null = unknown → keep prior
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Load connected BYO email mailboxes so the Business Email card reflects reality.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void fetch(apiUrl(`/api/morrow/email/accounts?userId=${encodeURIComponent(userId)}`), {
      headers: { accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { accounts?: unknown[] } | null) => {
        if (!cancelled && d && Array.isArray(d.accounts)) setEmailAccountCount(d.accounts.length);
      })
      .catch(() => {
        /* keep prior/null on transient error */
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    void fetchMcpServers().then((servers) => {
      if (!cancelled) setMcpServerCount(servers.length);
    });
    void fetchAppConnections().then((list) => {
      if (!cancelled) setAppConnections(list);
    });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn]);

  useEffect(() => {
    let cancelled = false;
    void fetchBuiltinApps().then((apps) => {
      if (!cancelled) setBuiltinApps(apps);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll whether a browser is paired + online so its card shows a live state.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const check = () =>
      fetch(apiUrl(`/api/morrow/browser/status?userId=${encodeURIComponent(userId)}`), {
        headers: { accept: "application/json" },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: { online?: boolean } | null) => {
          if (!cancelled && d) setBrowserOnline(Boolean(d.online));
        })
        .catch(() => {});
    void check();
    const t = setInterval(() => void check(), 8000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [userId]);

  // Debounce only the search box. Department chips refetch immediately so
  // clicking Comms/Dev/… doesn't wait on the query timer.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(trimmedQuery), 250);
    return () => window.clearTimeout(timer);
  }, [trimmedQuery]);

  // Query or department change -> reset to the FIRST page.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void fetchToolkitPage(debouncedQuery, department, 0, controller.signal)
      .then((page) => {
        setToolkits(page.toolkits);
        setTotal(page.total);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setToolkits([]);
        setTotal(0);
        setLoading(false);
        setError(err instanceof Error ? err.message : t("settings.connections.searchFailed"));
      });
    return () => {
      controller.abort();
    };
  }, [debouncedQuery, department, t]);

  // Track the loaded count in a ref so `loadMore` can read the next offset
  // without re-creating itself (and re-registering the observer) on every page.
  const loadedCountRef = useRef(0);
  loadedCountRef.current = toolkits.length;

  // Append the NEXT page. Offset is the number of cards already loaded, so the
  // server's [offset, offset + limit) slice is always contiguous. Guarded so a
  // fast scroll or button spam can't fire overlapping requests.
  const loadMore = useCallback(() => {
    if (loading || loadingMore) return;
    setLoadingMore(true);
    const controller = new AbortController();
    const offset = nextToolkitOffset(loadedCountRef.current);
    void fetchToolkitPage(debouncedQuery, department, offset, controller.signal)
      .then((page) => {
        // De-dupe defensively in case a page overlaps (e.g. catalog refresh).
        setToolkits((current) => {
          const seen = new Set(current.map((t) => t.slug));
          const next = page.toolkits.filter((t) => !seen.has(t.slug));
          return next.length > 0 ? [...current, ...next] : current;
        });
        setTotal(page.total);
        setLoadingMore(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setLoadingMore(false);
        setError(err instanceof Error ? err.message : t("settings.connections.loadMoreFailed"));
      });
  }, [loading, loadingMore, debouncedQuery, department, t]);

  // Infinite scroll: auto-load the next page when a sentinel below the grid
  // scrolls into view. The button remains as a fallback / explicit control.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current();
      },
      { rootMargin: "400px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, toolkits.length]);

  // Poll connections briefly after opening the OAuth popup so the row flips to
  // "Connected" once the flow finishes in the popup window.
  const pollRef = useRef<number | null>(null);
  const pollConnections = useCallback(
    (slug: string) => {
      let tries = 0;
      const tick = async () => {
        tries += 1;
        const slugs = await fetchConnectedSlugs(userId);
        if (slugs) setConnectedSlugs(slugs);
        if ((slugs?.has(slug) ?? false) || tries >= 20) {
          setConnectingSlug((current) => (current === slug ? null : current));
          return;
        }
        pollRef.current = window.setTimeout(() => void tick(), 1500);
      };
      pollRef.current = window.setTimeout(() => void tick(), 1500);
    },
    [userId],
  );
  useEffect(
    () => () => {
      if (pollRef.current !== null) window.clearTimeout(pollRef.current);
    },
    [],
  );

  const handleConnect = useCallback(
    async (slug: string) => {
      // Connecting requires a real signed-in user. A guest has no persistable
      // identity, so prompt Clerk sign-in instead of starting the OAuth flow.
      if (isClerkLoaded && !isSignedIn) {
        setSignInPrompt(true);
        clerk.openSignIn();
        return;
      }
      setConnectingSlug(slug);
      // Open the popup synchronously on click so the popup blocker allows it.
      const pendingPopup = openPendingConnectPopup();
      const outcome = await startConnectSlug(slug, userId);
      if (outcome.kind === "failed") {
        pendingPopup?.close();
        setConnectingSlug(null);
        setError(t("settings.connections.connectFailed"));
        return;
      }
      if (outcome.kind === "connected") {
        // No-auth toolkit: usable immediately. Close the placeholder popup and
        // refresh so the card flips to connected.
        pendingPopup?.close();
        {
          const s = await fetchConnectedSlugs(userId);
          if (s) setConnectedSlugs(s);
        }
        setConnectingSlug(null);
        return;
      }
      pointConnectPopup(pendingPopup, outcome.url);
      pollConnections(slug);
    },
    [userId, pollConnections, isClerkLoaded, isSignedIn, clerk, t],
  );

  // One-click disconnect: delete the user's connected account(s) for this slug,
  // then refresh the connected set so the card flips back to "Connect".
  const handleDisconnect = useCallback(
    async (slug: string) => {
      setDisconnectingSlug(slug);
      try {
        await startDisconnect(userId, slug);
        {
          const s = await fetchConnectedSlugs(userId);
          if (s) setConnectedSlugs(s);
        }
      } finally {
        setDisconnectingSlug((current) => (current === slug ? null : current));
      }
    },
    [userId],
  );

  return (
    <SettingsPageContainer>
      <SettingsSection
        {...searchableSetting("remote-environments", t)}
        title={t("settings.section.connections")}
      >
        <SettingsRow
          title={t("settings.connections.rowTitle")}
          description={t("settings.connections.rowDescription")}
        />

        <div className="px-3 sm:px-4">
          <div className="flex h-10 items-center gap-2 rounded-lg border border-border/60 bg-background px-3">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground/70" />
            <Input
              nativeInput
              unstyled
              type="search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={t("settings.connections.searchPlaceholder")}
              aria-label={t("settings.connections.searchAria")}
              className="min-w-0 flex-1 [&_[data-slot=input]]:h-auto [&_[data-slot=input]]:p-0 [&_[data-slot=input]]:text-sm"
            />
            {loading ? (
              <Loader2Icon className="size-4 shrink-0 animate-spin text-muted-foreground/70" />
            ) : null}
          </div>

          <div
            role="toolbar"
            aria-label={t("settings.connections.departmentsAria")}
            className="mt-3 flex flex-wrap gap-1.5"
          >
            {TOOLKIT_DEPARTMENT_FILTERS.map((filter) => {
              const Icon = DEPARTMENT_ICONS[filter.id];
              const selected = department === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setDepartment(filter.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition",
                    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                    selected
                      ? "border-primary bg-primary text-primary-foreground [&_svg]:text-primary-foreground!"
                      : "border-border/60 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                  )}
                >
                  <Icon className="size-3.5" aria-hidden="true" />
                  {t(filter.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {signInPrompt && isClerkLoaded && !isSignedIn ? (
          <div className="px-3 sm:px-4">
            <div className="card-glass flex items-center justify-between gap-3 rounded-lg border border-transparent px-3 py-2">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground">
                <LogInIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                {t("settings.connections.signInToConnect")}
              </span>
              <button
                type="button"
                onClick={() => clerk.openSignIn()}
                className="rounded-md border border-border bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("common.signIn")}
              </button>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="px-3 sm:px-4">
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          </div>
        ) : null}

        <div className="mt-4 px-3 sm:px-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
              {t("settings.connections.loading")}
            </div>
          ) : toolkits.length === 0 && visibleBuiltinApps.length === 0 && !error ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {trimmedQuery.length > 0
                ? t("settings.connections.noMatch", { query: trimmedQuery })
                : department !== "all"
                  ? t("settings.connections.noInDepartment", {
                      department: t(
                        TOOLKIT_DEPARTMENT_FILTERS.find((filter) => filter.id === department)
                          ?.labelKey ?? "settings.connections.dept.other",
                      ),
                    })
                  : t("settings.connections.none")}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {/* BYO email sits first, styled as a connector card. Shown only in
                    the unfiltered "All" view so a keyword/department search isn't
                    polluted by a card that doesn't match it. */}
                {visibleBuiltinApps.map((app) => (
                  <BuiltinAppCard
                    key={app.id}
                    app={app}
                    connection={appConnections.find((c) => c.appId === app.id)}
                    disconnecting={disconnectingApp === app.id}
                    onConnect={() => handleBuiltinConnect(app)}
                    onDisconnect={() => void handleBuiltinDisconnect(app.id)}
                  />
                ))}
                {department === "all" && debouncedQuery.trim() === "" ? (
                  <>
                    <EmailConnectCard connectedCount={emailAccountCount ?? 0} />
                    <BrowserConnectCard online={browserOnline} />
                  </>
                ) : null}
                {department === "all" && debouncedQuery.trim() === "" ? (
                  <>
                    <WhatsAppConnectCard />
                    <PlaudConnectCard />
                    <McpConnectCard connectedCount={mcpServerCount} />
                  </>
                ) : null}
                {toolkits.map((toolkit) => (
                  <ToolkitCard
                    key={toolkit.slug}
                    toolkit={toolkit}
                    connected={connectedSlugs.has(toolkit.slug)}
                    connecting={connectingSlug === toolkit.slug}
                    disconnecting={disconnectingSlug === toolkit.slug}
                    onConnect={() => void handleConnect(toolkit.slug)}
                    onDisconnect={() => void handleDisconnect(toolkit.slug)}
                  />
                ))}
              </div>

              {/* Sentinel: intersecting it auto-loads the next page. */}
              <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />

              {hasMore ? (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="button-glass inline-flex items-center gap-2 rounded-lg border-transparent px-4 py-2 text-xs font-semibold transition hover:brightness-[0.98] disabled:opacity-60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
                        {t("common.loading")}
                      </>
                    ) : (
                      t("settings.connections.loadMore", {
                        loaded: toolkits.length,
                        total,
                      })
                    )}
                  </button>
                </div>
              ) : (
                <p className="mt-4 text-center text-xs text-muted-foreground/60 uppercase">
                  {t("settings.connections.count", { count: toolkits.length })}
                </p>
              )}
            </>
          )}
        </div>
      </SettingsSection>
      <AppConnectDialog
        app={connectApp}
        onOpenChange={(open) => {
          if (!open) setConnectApp(null);
        }}
        onConnected={(connection) => {
          setAppConnections((list) => [
            ...list.filter((c) => c.appId !== connection.appId),
            connection,
          ]);
          setConnectApp(null);
        }}
      />
    </SettingsPageContainer>
  );
}
